/**
 * Timesheet — CATS OData <-> app-domain mapper (pure).
 *
 * Phase 3 (Option A). Everything here is a pure function so the contract tests
 * can assert the exact request/response shapes against `$metadata` without a
 * live SAP system. The OData transport lives in `catsOData.ts`; the adapter that
 * wires the two together is `timesheetCatsService.ts`.
 *
 * The names below are the frozen `$metadata` surface (entity sets, TimeSheet /
 * ApprovalSegment fields, action names + params). The contract test cross-checks
 * every one of them against the captured EDMX so a server rename can never drift
 * silently from this adapter.
 */
import type {
  TsTimeEntry,
  TsSubmission,
  ApprovalDecision,
} from '../types/timesheet.types'
import type { PendingApprovalSegment } from './timesheetSupabaseService'
import { addDays, toIsoDate } from './timesheetMockData'
import { sapChargeObjectId, normalizeWbsKey, type SapObjectType } from '../lib/sapChargeObjects'

// ─── $metadata surface (asserted by the contract test) ───────────────────────

export const CATS_ENTITY = {
  TimeSheet: 'TimeSheet',
  ApprovalSegment: 'ApprovalSegment',
  Attestation: 'Attestation',
  ESignature: 'ESignature',
  LegalHold: 'LegalHold',
} as const

export const CATS_ACTION = {
  createEntry: 'createEntry',
  submit: 'submit',
  approve: 'approve',
  reject: 'reject',
  recall: 'recall',
  decideSegment: 'decideSegment',
} as const

/** Every field on the `TimeSheet` entity (the CATS time record). */
export const TIMESHEET_FIELDS = [
  'TimeSheetRecord', 'Pernr', 'WorkDate', 'RecordedHours', 'HoursUnit',
  'WbsElement', 'WbsInternalId', 'ActivityType', 'SenderCostCenter',
  'ReceiverCostCenter', 'InternalOrder', 'CompanyCode', 'ControllingArea',
  'CatsStatus', 'ShortText', 'ApprovedByUser', 'ApprovedDate', 'CreatedByUser',
  'SubmissionId', 'EntryId', 'Clin', 'Acrn', 'PopStart', 'PopEnd', 'WorkSite',
  'ColorOfMoney',
] as const

/** Every field on the `ApprovalSegment` entity (the routing overlay). */
export const APPROVAL_SEGMENT_FIELDS = [
  'SegmentUuid', 'SubmissionId', 'Pernr', 'ApproverId', 'WeekStart',
  'ChargeObject', 'SubmittedAt', 'Decision', 'DecidedBy', 'DecidedAt',
  'ReasonCode', 'DelegatedFrom', 'DelegatedTo', 'SegmentHours', 'CreatedAt',
] as const

/** Parameter names per bound action (the JSON body keys), from `$metadata`. */
export const ACTION_PARAMS = {
  createEntry: ['Pernr', 'WorkDate', 'RecordedHours', 'WbsElement', 'ActivityType', 'AbsenceType', 'SenderCostCenter', 'RecOrder', 'ShortText', 'Profile', 'ReleaseFlag', 'SubmissionId', 'EntryId'],
  submit: ['Pernr', 'WeekStart', 'Profile', 'SubmissionId', 'ReasonCode', 'Actor'],
  approve: ['Pernr', 'WeekStart', 'Profile', 'SubmissionId', 'ReasonCode', 'Actor'],
  reject: ['Pernr', 'WeekStart', 'Profile', 'SubmissionId', 'ReasonCode', 'Actor'],
  recall: ['Pernr', 'WeekStart', 'Profile', 'SubmissionId', 'ReasonCode', 'Actor'],
  decideSegment: ['SegmentUuid', 'Decision', 'Actor', 'ReasonCode'],
} as const

/** The columns the read path selects for a week of time (a lean projection). */
export const TIMESHEET_SELECT: readonly string[] = [
  'TimeSheetRecord', 'Pernr', 'WorkDate', 'RecordedHours', 'HoursUnit',
  'WbsElement', 'WbsInternalId', 'ActivityType', 'SenderCostCenter',
  'InternalOrder', 'CatsStatus', 'ShortText', 'SubmissionId', 'EntryId',
  'Clin', 'Acrn', 'WorkSite', 'ColorOfMoney',
]

// ─── Row shapes (JSON returned by the service) ───────────────────────────────

export interface TimeSheetRow {
  TimeSheetRecord: string
  Pernr: string
  WorkDate: string
  RecordedHours: number | string
  HoursUnit?: string
  WbsElement?: string
  WbsInternalId?: string
  ActivityType?: string
  SenderCostCenter?: string
  ReceiverCostCenter?: string
  InternalOrder?: string
  CompanyCode?: string
  ControllingArea?: string
  CatsStatus?: string
  ShortText?: string
  ApprovedByUser?: string
  ApprovedDate?: string
  CreatedByUser?: string
  SubmissionId?: string
  EntryId?: string
  Clin?: string
  Acrn?: string
  PopStart?: string
  PopEnd?: string
  WorkSite?: string
  ColorOfMoney?: string
}

export interface ApprovalSegmentRow {
  SegmentUuid: string
  SubmissionId?: string
  Pernr?: string
  ApproverId?: string
  WeekStart?: string
  ChargeObject?: string
  SubmittedAt?: string
  Decision?: string
  DecidedBy?: string
  DecidedAt?: string
  ReasonCode?: string
  DelegatedFrom?: string
  DelegatedTo?: string
  SegmentHours?: number | string
  CreatedAt?: string
}

// ─── Status: CATS 10/20/30/50/60 <-> app state machine ───────────────────────
// Handoff §5 (authoritative): 10/20 -> pending, 30 -> approved,
// 60 -> posted (still 'approved' in the app's ApprovalDecision enum, which has
// no 'posted' member). 50 (seen live) is treated as pending. 'rejected' is not
// a CATS status (reject resets to 20); it is carried on the ApprovalSegment.

export function catsStatusToApp(status: string | undefined): ApprovalDecision {
  switch ((status ?? '').trim()) {
    case '30':
    case '60':
      return 'approved'
    case '10':
    case '20':
    case '50':
    default:
      return 'pending'
  }
}

/** Aggregate a submission's per-record statuses into one app status. Any
 *  pending record keeps the whole submission pending; all approved -> approved. */
export function aggregateSubmissionStatus(statuses: ApprovalDecision[]): ApprovalDecision {
  if (statuses.length === 0) return 'pending'
  if (statuses.some((s) => s === 'rejected')) return 'rejected'
  if (statuses.every((s) => s === 'approved')) return 'approved'
  return 'pending'
}

// ─── Charge-object <-> synthetic charge-code id ──────────────────────────────
// A CATS row charges to exactly one receiver: a WBS element, an order, or a
// (sender) cost center. Map it to the same synthetic id the SAP charge-object
// catalog produces (`<type>:<key>`), so getChargeCode resolves the label from
// the primed reference cache and historical entries still render.

//
// An order line the shop floor clock posted is one line per ORDER + OPERATION:
// the clock posts a separate CATS record for each operation of an order, and
// two of them on the same day must not share a grid cell. Standard CATS carries
// no routing operation for a production order, so it is read from the record's
// short text and appended to the id as `<type>:<order>/<operation>`.

/** Separates the order from its operation inside a charge-code id. */
const ORDER_OPERATION_SEP = '/'

/** The routing operation a shop floor clock record was posted for, or ''. The
 *  clock writes the short text as "Op 0020 shop floor clock". */
export function shopFloorOperation(row: Pick<TimeSheetRow, 'ShortText'>): string {
  const m = /^Op (\S{1,4}) shop floor clock$/i.exec((row.ShortText ?? '').trim())
  return m ? m[1]! : ''
}

export function chargeCodeIdForRow(row: TimeSheetRow): string {
  const wbs = (row.WbsElement ?? '').trim()
  if (wbs) return sapChargeObjectId('wbs_element', normalizeWbsKey(wbs))
  const order = (row.InternalOrder ?? '').trim()
  if (order && Number(order) !== 0) {
    const operation = shopFloorOperation(row)
    const key = normalizeWbsKey(order)
    return sapChargeObjectId('internal_order', operation ? `${key}${ORDER_OPERATION_SEP}${operation}` : key)
  }
  const cc = (row.SenderCostCenter ?? '').trim()
  if (cc) return sapChargeObjectId('cost_center', cc)
  return ''
}

/** Split a synthetic charge-code id (`<type>:<key>`) back into its parts. Legacy
 *  seed ids (e.g. `ts-cc-001`) have no `:` and resolve to a bare WBS key. For an
 *  order + operation line, `key` is the ORDER alone (what SAP posts against) and
 *  the operation comes back separately. */
export function splitChargeCodeId(id: string): { type: SapObjectType | null; key: string; operation?: string } {
  const i = (id ?? '').indexOf(':')
  if (i < 0) return { type: null, key: id ?? '' }
  const type = id.slice(0, i) as SapObjectType
  const key = id.slice(i + 1)
  const known: SapObjectType[] = ['wbs_element', 'network_activity', 'internal_order', 'cost_center', 'production_order']
  if (!known.includes(type)) return { type: null, key }
  // Only an order carries an operation: a WBS key may itself contain the separator.
  const sep = type === 'internal_order' || type === 'production_order' ? key.indexOf(ORDER_OPERATION_SEP) : -1
  if (sep < 0) return { type, key }
  return { type, key: key.slice(0, sep), operation: key.slice(sep + 1) }
}

/**
 * A CATS record the shop floor clock posted for a production order. Its hours
 * are the clock's, so the grid shows it read only: it changes through the
 * clock (a supervisor void or correction), never by typing over it. The
 * non-production line of the same shift is NOT one of these and stays editable.
 */
export function isShopFloorClockRecord(row: Pick<TimeSheetRow, 'ShortText'>): boolean {
  return /shop floor clock$/i.test((row.ShortText ?? '').trim())
}

// ─── Row -> TsTimeEntry ──────────────────────────────────────────────────────

/**
 * Map a `TimeSheet` row to the app's time entry. `employeeIdOverride` (the value
 * the caller queried with) is preserved as `employeeId` so the grid's filters
 * keep matching even though the service returns the un-padded PERNR (e.g. "8").
 */
export function timeSheetRowToEntry(row: TimeSheetRow, employeeIdOverride?: string): TsTimeEntry {
  const submissionId = (row.SubmissionId ?? '').trim()
  return {
    // The CATS counter (TimeSheetRecord) is the entry id. It flows back as the
    // action's EntryId on the next edit/delete, where Z_M12_CATS_POST recognizes
    // it as an existing record and routes the mutation through
    // BAPI_CATIMESHEETMGR_CHANGE (against this counter) — an in-place edit or a
    // change-to-0 delete that keeps the CATS audit chain (REFCOUNTER), instead of
    // an append or a hard delete. A brand-new cell carries a non-numeric `te-…`
    // id, which the wrapper treats as an INSERT.
    id: row.TimeSheetRecord,
    employeeId: employeeIdOverride ?? row.Pernr,
    date: row.WorkDate,
    chargeCodeId: chargeCodeIdForRow(row),
    laborCategory: (row.ActivityType ?? '').trim(),
    hours: Number(row.RecordedHours ?? 0),
    notes: (row.ShortText ?? '').trim() || null,
    submissionId: submissionId || null,
    source: isShopFloorClockRecord(row) ? 'wand' : 'import',
    customerId: (row.Clin ?? '').trim() || null,
  }
}

// ─── TsTimeEntry -> createEntry action params ────────────────────────────────

export interface CreateEntryParams {
  Pernr: string
  WorkDate: string
  RecordedHours: number
  WbsElement: string
  ActivityType: string
  AbsenceType: string
  SenderCostCenter: string
  RecOrder: string
  ShortText: string
  Profile: string
  ReleaseFlag: boolean
  SubmissionId: string
  EntryId: string
}

/**
 * Build the `createEntry` bound-action body from an app time entry. The charge
 * object is derived from the entry's charge-code id: a WBS goes to `WbsElement`,
 * an order to `RecOrder`, a cost center to `SenderCostCenter`. `Profile` is the
 * CATS data-entry profile (Phase 2's one open item — supplied by config).
 */
export function entryToCreateEntryParams(
  entry: TsTimeEntry,
  opts: { profile: string; release?: boolean; hoursOverride?: number },
): CreateEntryParams {
  const { type, key } = splitChargeCodeId(entry.chargeCodeId)
  return {
    Pernr: entry.employeeId,
    WorkDate: entry.date,
    RecordedHours: opts.hoursOverride ?? entry.hours,
    WbsElement: type === 'wbs_element' || type === null ? key : '',
    ActivityType: entry.laborCategory || '',
    // PTO / leave lines carry the SAP absence type (AWART) resolved from the
    // ts_pto_types config; the wrapper FM forwards it to CATS `abs_att_type` so
    // the record is a real leave record (not just a WBS charge). Empty for work.
    AbsenceType: entry.absenceType ?? '',
    SenderCostCenter: type === 'cost_center' ? key : '',
    RecOrder: type === 'internal_order' || type === 'production_order' ? key : '',
    ShortText: entry.notes ?? '',
    Profile: opts.profile,
    ReleaseFlag: !!opts.release,
    SubmissionId: entry.submissionId ?? '',
    EntryId: entry.id,
  }
}

// ─── ApprovalSegment row -> PendingApprovalSegment ───────────────────────────

/**
 * Map an `ApprovalSegment` row to the frozen `PendingApprovalSegment`.
 * `segmentHours` is computed client-side (the CDS carries a 0 placeholder by
 * design — see handoff §3); the caller passes the summed week hours in.
 */
export function segmentRowToPending(row: ApprovalSegmentRow, segmentHours: number): PendingApprovalSegment {
  return {
    segmentId: row.SegmentUuid,
    submissionId: (row.SubmissionId ?? '').trim(),
    approverUserId: (row.ApproverId ?? '').trim(),
    employeeId: (row.Pernr ?? '').trim(),
    weekStart: (row.WeekStart ?? '').trim(),
    submittedAt: (row.SubmittedAt ?? '').trim() || new Date(0).toISOString(),
    segmentHours,
  }
}

// ─── Rows -> derived submissions ─────────────────────────────────────────────
// There is no server "submission" entity; a submission is the set of CATS
// records sharing a `SubmissionId` (written into ZTM12_TS_XREF at submit). Group
// the week's rows by SubmissionId and synthesize a TsSubmission per group.

export function deriveSubmissions(rows: TimeSheetRow[], employeeIdOverride?: string): TsSubmission[] {
  const groups = new Map<string, TimeSheetRow[]>()
  for (const r of rows) {
    const sid = (r.SubmissionId ?? '').trim()
    if (!sid) continue
    const g = groups.get(sid)
    if (g) g.push(r)
    else groups.set(sid, [r])
  }
  const out: TsSubmission[] = []
  for (const [sid, rs] of groups) {
    const totalHours = round2(rs.reduce((s, r) => s + Number(r.RecordedHours ?? 0), 0))
    const weekStart = rs.map((r) => r.WorkDate).filter(Boolean).sort()[0] ?? ''
    const status = aggregateSubmissionStatus(rs.map((r) => catsStatusToApp(r.CatsStatus)))
    out.push({
      id: sid,
      employeeId: employeeIdOverride ?? (rs[0]?.Pernr ?? ''),
      weekStart,
      totalHours,
      status,
      // No submit timestamp on a CATS row; anchor to the earliest work day.
      submittedAt: weekStart ? `${weekStart}T00:00:00Z` : new Date(0).toISOString(),
      anomalyCount: rs.filter((r) => Number(r.RecordedHours ?? 0) > 10).length,
    })
  }
  return out.sort((a, b) => b.weekStart.localeCompare(a.weekStart))
}

// ─── OData $filter builders ──────────────────────────────────────────────────

/** `Pernr eq '<pernr>' and WorkDate ge <start> and WorkDate lt <start+7>`.
 *  Edm.Date literals are unquoted in V4; the PERNR string is quoted. Window is
 *  [weekStart, weekStart+7) to match the mock/Supabase backends exactly. */
export function buildWeekFilter(pernr: string, weekStart: string): string {
  const end = toIsoDate(addDays(new Date(weekStart), 7))
  return `Pernr eq ${odataString(pernr)} and WorkDate ge ${weekStart} and WorkDate lt ${end}`
}

/** Escape a string for an OData V4 literal (single-quote doubling). */
export function odataString(v: string): string {
  return `'${String(v).replace(/'/g, "''")}'`
}

function round2(n: number): number {
  return Number(n.toFixed(2))
}
